<template>
  <Transition name="fade">
    <div class="fixed inset-0 z-[100000] flex items-center justify-center" data-test="zendesk-modal">
      <div class="fixed inset-0 bg-black/40" @click="close"></div>

      <div class="bg-white rounded-md shadow-lg w-[min(1100px,95vw)] max-h-[92vh] z-50 flex flex-col">
        <div class="flex items-center justify-between px-6 pt-5 pb-3 border-b border-gray-200">
          <div>
            <h3 class="text-lg font-medium">Zendesk — Order #{{ orderNo }}</h3>
            <p class="text-xs text-gray-500 mt-0.5">
              <span v-if="isLoading">Looking up conversations…</span>
              <span v-else-if="data.fetched_at">Fetched {{ formatDateTime(data.fetched_at) }} · read-only, reply in Zendesk</span>
            </p>
          </div>
          <div class="flex items-center gap-2">
            <button
              class="text-sm px-3 py-1.5 border rounded-md text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              :disabled="isLoading"
              data-test="zendesk-refresh"
              @click="reload"
            >
              Refresh
            </button>
            <button class="text-gray-500 hover:text-gray-800 text-xl leading-none px-2" title="Close" data-test="zendesk-close" @click="close">×</button>
          </div>
        </div>

        <div class="px-6 pb-5 overflow-y-auto grow">
          <p v-if="error" class="mt-4 rounded bg-amber-50 border border-amber-200 text-amber-800 text-sm px-3 py-2" data-test="zendesk-error">
            {{ error }}
          </p>

          <UnderlineTabs :tabs="tabs" :initialTab="activeTab" @change="onTab">
            <template #customer>
              <TicketList
                :tickets="customer"
                :loading="isLoading"
                empty="No customer conversations found for this order."
                kind="customer"
              />
            </template>
            <template #factory>
              <TicketList
                :tickets="factory"
                :loading="isLoading"
                empty="No factory conversations found for this order."
                kind="factory"
              />
            </template>
          </UnderlineTabs>
        </div>
      </div>
    </div>
  </Transition>
</template>

<script setup lang="ts">
import { computed, defineComponent, h, onMounted, PropType, ref, VNode } from 'vue';
import UnderlineTabs from '@/components/Shared/UnderlineTabs.vue';
import {
  formatBytes,
  formatDateTime,
  personLabel,
  sanitizeHtml,
  statusClass,
  useZendeskTickets,
} from '@/composables/useZendeskTickets';
import { ZendeskComment, ZendeskTab, ZendeskTicket } from '@/models';

const props = defineProps<{
  orderId: number;
  orderNo: string;
}>();

const emit = defineEmits<{ (e: 'close'): void }>();

const { data, customer, factory, counts, isLoading, error, load } = useZendeskTickets();

const activeTab = ref<ZendeskTab>('customer');
const tabs = computed(() => [
  { id: 'customer', label: `Customer (${counts.value.customer})` },
  { id: 'factory', label: `Factory (${counts.value.factory})` },
]);
const onTab = (id: string) => {
  activeTab.value = id as ZendeskTab;
};

const close = () => emit('close');
const reload = () => load(props.orderId, true);

onMounted(() => {
  load(props.orderId);
});

// ---------------------------------------------------------------- ticket list (render function keeps this file self-contained)

const TicketList = defineComponent({
  name: 'ZendeskTicketList',
  props: {
    tickets: { type: Array as PropType<ZendeskTicket[]>, required: true },
    loading: { type: Boolean, default: false },
    empty: { type: String, default: 'Nothing here.' },
    kind: { type: String as PropType<ZendeskTab>, required: true },
  },
  setup(p) {
    const expanded = ref<Set<number>>(new Set());
    const showHtml = ref<Set<number>>(new Set());
    const toggle = (id: number) => {
      const next = new Set(expanded.value);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      expanded.value = next;
    };
    const toggleHtml = (id: number) => {
      const next = new Set(showHtml.value);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      showHtml.value = next;
    };

    const renderComment = (c: ZendeskComment): VNode =>
      h(
        'div',
        {
          key: c.id,
          class: ['rounded-md border px-3 py-2 text-sm', c.public ? 'border-gray-200 bg-white' : 'border-amber-200 bg-amber-50'],
          'data-test': 'zendesk-comment',
        },
        [
          h('div', { class: 'flex flex-wrap items-center gap-x-2 text-xs text-gray-500 mb-1' }, [
            h('span', { class: 'font-medium text-gray-800' }, personLabel(c.author)),
            c.author?.email ? h('span', `<${c.author.email}>`) : null,
            h('span', '·'),
            h('span', formatDateTime(c.created_at)),
            !c.public ? h('span', { class: 'ml-1 rounded bg-amber-200 text-amber-900 px-1.5 py-0.5', 'data-test': 'zendesk-internal' }, 'Internal note') : null,
          ]),
          !showHtml.value.has(c.id) && c.html_body
            ? h('div', { class: 'zendesk-html prose prose-sm max-w-none', innerHTML: sanitizeHtml(c.html_body) })
            : h('div', { class: 'whitespace-pre-wrap break-words text-gray-800' }, c.plain_body || ''),
          c.html_body && c.html_body !== c.plain_body
            ? h(
                'button',
                { class: 'mt-1 text-xs text-[#009FE3] hover:underline', type: 'button', onClick: () => toggleHtml(c.id) },
                !showHtml.value.has(c.id) ? 'Show plain text' : 'Show formatted'
              )
            : null,
          c.attachments.length
            ? h(
                'ul',
                { class: 'mt-2 flex flex-wrap gap-2' },
                c.attachments.map((a) =>
                  h('li', { key: a.id }, [
                    h(
                      'a',
                      {
                        href: a.url,
                        target: '_blank',
                        rel: 'noopener noreferrer',
                        class: 'inline-flex items-center gap-1 rounded border border-gray-200 bg-gray-50 px-2 py-1 text-xs text-gray-700 hover:bg-gray-100',
                        'data-test': 'zendesk-attachment',
                      },
                      `📎 ${a.name} (${formatBytes(a.size)})`
                    ),
                  ])
                )
              )
            : null,
        ]
      );

    const renderTicket = (t: ZendeskTicket): VNode => {
      const open = !expanded.value.has(t.id);
      return h('div', { key: t.id, class: 'rounded-md border border-gray-200 mb-3', 'data-test': `zendesk-ticket-${p.kind}` }, [
        h('div', { class: 'flex items-start justify-between gap-3 px-4 py-3 cursor-pointer hover:bg-gray-50', onClick: () => toggle(t.id), 'data-test': 'zendesk-ticket-toggle' }, [
          h('div', { class: 'min-w-0' }, [
            h('div', { class: 'flex items-center gap-2 flex-wrap' }, [
              h('span', { class: `rounded px-2 py-0.5 text-xs font-medium uppercase ${statusClass(t.status)}`, 'data-test': 'zendesk-status' }, t.status),
              h('span', { class: 'font-medium text-gray-900 truncate' }, t.subject || '(no subject)'),
              h('span', { class: 'text-xs font-semibold text-gray-700 bg-gray-100 rounded px-1.5 py-0.5 whitespace-nowrap' }, `Ticket #${t.id}`),
              h('span', {
                class: (!!props.orderNo && (((t.subject || '') + ' ' + (t.comments || []).map(function(c){ return (c.plain_body || '') + ' ' + (c.html_body || ''); }).join(' ')).indexOf(props.orderNo) !== -1))
                  ? 'text-xs font-semibold text-green-700 bg-green-100 rounded px-1.5 py-0.5 whitespace-nowrap'
                  : 'text-xs font-semibold text-amber-700 bg-amber-100 rounded px-1.5 py-0.5 whitespace-nowrap',
                title: (!!props.orderNo && (((t.subject || '') + ' ' + (t.comments || []).map(function(c){ return (c.plain_body || '') + ' ' + (c.html_body || ''); }).join(' ')).indexOf(props.orderNo) !== -1)) ? 'Matched by order number' : 'Matched by name only - please verify',
              }, (!!props.orderNo && (((t.subject || '') + ' ' + (t.comments || []).map(function(c){ return (c.plain_body || '') + ' ' + (c.html_body || ''); }).join(' ')).indexOf(props.orderNo) !== -1)) ? 'Confirmed' : 'Probable'),
            ]),
            h('div', { class: 'text-xs text-gray-500 mt-1' }, [
              `${p.kind === 'factory' ? 'Raised by' : 'From'} ${personLabel(t.requester)}`,
              t.assignee ? ` · Assigned to ${personLabel(t.assignee)}` : '',
              ` · ${t.comments.length} message${t.comments.length === 1 ? '' : 's'}`,
              ` · Updated ${formatDateTime(t.updated_at)}`,
            ].join('')),
          ]),
          h('div', { class: 'flex items-center gap-3 shrink-0' }, [
            h(
              'a',
              { href: t.url, target: '_blank', rel: 'noopener noreferrer', class: 'text-sm text-[#009FE3] hover:underline whitespace-nowrap', 'data-test': 'zendesk-open', onClick: (e: Event) => e.stopPropagation() },
              'Open in Zendesk ↗'
            ),
            h('span', { class: 'text-gray-400 text-sm' }, open ? '▲' : '▼'),
          ]),
        ]),
        open
          ? h('div', { class: 'border-t border-gray-100 px-4 py-3 flex flex-col gap-2 bg-gray-50/40', 'data-test': 'zendesk-thread' }, t.comments.map((c) => renderComment(c)))
          : null,
      ]);
    };

    return () => {
      if (p.loading && p.tickets.length === 0) {
        return h('div', { class: 'py-8 text-center text-sm text-gray-500' }, 'Loading…');
      }
      if (p.tickets.length === 0) {
        return h('div', { class: 'py-8 text-center text-sm text-gray-500', 'data-test': `zendesk-empty-${p.kind}` }, p.empty);
      }
      return h('div', p.tickets.map((t) => renderTicket(t)));
    };
  },
});
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.15s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
:deep(.zendesk-html) img {
  max-width: 100%;
  height: auto;
}
:deep(.zendesk-html) a {
  color: #009fe3;
  text-decoration: underline;
}
:deep(.zendesk-html) blockquote {
  border-left: 3px solid #e5e7eb;
  padding-left: 0.75rem;
  color: #6b7280;
}
</style>
